import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { AlertDialog } from '../dialog';
import * as gglassActions from '../../store/gglass';
import * as gtypeDetailActions from '../../store/gtypeDetail';

const isProductSlot = (component) => (
  component?.type === 'property'
  && Array.isArray(component.properties)
  && component.properties.length > 0
  && component.properties.every((property) => property.applyType === 'productName')
);

/** 프로젝트 유리 구성과 같은 칸 너비. 넓은 3등분 칸 대신 한 줄에 붙인다. */
const slotWidth = (code, multiple) => {
  if (multiple) return 140;
  switch (code) {
    case 'GLASS PRODUCT NAME': return 150;
    case 'GL THICKNESS': return 98;
    case 'GL HEAT TREATING': return 96;
    case 'GAS MATERIAL': return 98;
    case 'FILM TYPE': return 100;
    case 'FILM THICKNESS': return 100;
    case 'SPACER THICKNESS': return 114;
    default: return 110;
  }
};

const paneField = (code) => (
  code !== 'SPACER THICKNESS'
  && code !== 'GAS MATERIAL'
  && code !== 'FILM THICKNESS'
  && code !== 'FILM TYPE'
);

/** 등급 칸과 같은 높이. 패딩을 줄이면 옆 칸과 수평이 어긋난다. */
const controlHeightSx = {
  '& .MuiInputLabel-root': { fontSize: '0.9rem' },
  '& .MuiOutlinedInput-root': { height: 40 },
  '& .MuiSelect-select': {
    fontSize: '0.9rem',
    minHeight: '0 !important',
    display: 'flex',
    alignItems: 'center',
    py: 0,
  },
};

const fieldSx = (code, multiple) => ({
  width: slotWidth(code, multiple),
  maxWidth: '100%',
  flex: '0 0 auto',
  bgcolor: paneField(code) ? '#E8F3FF' : undefined,
  ...(multiple ? { '& .MuiInputLabel-root': { fontSize: '0.9rem' } } : controlHeightSx),
});

const rowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1,
};

const cellValueId = (cell) => {
  if (!cell || Array.isArray(cell.value)) return '';
  return cell.value?.id || '';
};

const cellValueIds = (cell) => {
  if (!cell) return [];
  if (Array.isArray(cell.value)) return cell.value.map((item) => item?.id).filter(Boolean);
  return cell.value?.id ? [cell.value.id] : [];
};

const dependentOf = (cell) => {
  const raw = cell?.value?.dependentGcomponentItem;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
};

/** 가공 말단의 유리 템플릿과 원판 카탈로그 칸. 품종은 고른 인증규격 id로 찾는다. */
const SubMatFolderGlassBom = ({
  certificationId,
  value,
  onChange,
  readOnly = false,
  showTitle = true,
  inline = false,
}) => {
  const [glasses, setGlasses] = useState([]);
  const [layers, setLayers] = useState([]);
  const [alertInfo, setAlertInfo] = useState({});

  useEffect(() => {
    if (!certificationId) {
      setGlasses([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await gglassActions.selectAllByQueryDirect();
        const matched = (list || []).filter((glass) => (
          (glass.selectedG04docuGCertifications || []).some((cert) => (
            (cert.id || cert.g04docuGCertificationId) === certificationId && cert.checked === true
          ))
        ));
        if (!cancelled) setGlasses(matched);
      } catch (_err) {
        if (!cancelled) setGlasses([]);
      }
    })();
    return () => { cancelled = true; };
  }, [certificationId]);

  useEffect(() => {
    const gtypeId = value?.gtypeId || glasses.find((glass) => glass.id === value?.gglassId)?.gtypeId;
    if (!gtypeId) {
      setLayers([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await gtypeDetailActions.selectGTypeDetailsWithGComponentDirect(gtypeId);
        if (!cancelled) setLayers(Array.isArray(detail) ? detail : []);
      } catch (_err) {
        if (!cancelled) setLayers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [value?.gglassId, value?.gtypeId, glasses]);

  const handleTemplate = (gglassId) => {
    const glass = glasses.find((item) => item.id === gglassId);
    if (!glass) {
      onChange?.(null);
      return;
    }
    onChange?.({
      gglassId: glass.id,
      gtypeId: glass.gtypeId || null,
      selectedGcomponentItems: Array.isArray(glass.selectedGcomponentItems)
        ? glass.selectedGcomponentItems
        : [],
    });
  };

  const writeCell = (layerIndex, slotIndex, component, cellValue) => {
    const rows = (value?.selectedGcomponentItems || []).map((row) => (Array.isArray(row) ? [...row] : []));
    while (rows.length <= layerIndex) rows.push([]);
    while (rows[layerIndex].length <= slotIndex) rows[layerIndex].push(null);
    rows[layerIndex][slotIndex] = {
      id: component.id,
      name: component.name,
      code: component.code,
      value: cellValue,
    };
    onChange?.({
      gglassId: value?.gglassId,
      gtypeId: value?.gtypeId,
      selectedGcomponentItems: rows,
    });
  };

  const handleSlot = (layerIndex, slotIndex, component, propertyId) => {
    const property = (component.properties || []).find((item) => item.id === propertyId) || '';
    const rows = (value?.selectedGcomponentItems || []).map((row) => (Array.isArray(row) ? [...row] : []));
    while (rows.length <= layerIndex) rows.push([]);
    const layer = rows[layerIndex];
    if (!property) {
      const blocked = layer.find((item) => dependentOf(item)?.gcomponentId === component.id);
      if (blocked) {
        setAlertInfo({
          titleAlert: '안내',
          open: true,
          messageAlert: (
            <span>
              <span style={{ color: '#1976d2' }}>{`'${blocked.name}'`}</span>
              {'(이)가 \''}
              <span style={{ color: '#1976d2' }}>{`${blocked.value?.name || ''}`}</span>
              {'\'(으)로 선택된 경우 '}
              <span style={{ color: '#1976d2' }}>{`'${component.name}'`}</span>
              {'(은)는 '}
              <br />
              <span style={{ color: '#d32f2f' }}><i><u>반드시 값을 선택</u></i></span>
              {'해야 합니다.'}
            </span>
          ),
        });
        return;
      }
    }
    while (layer.length <= slotIndex) layer.push(null);
    const previous = layer[slotIndex];
    layer[slotIndex] = {
      id: component.id,
      name: component.name,
      code: component.code,
      value: property,
    };
    const applyDependent = (dep, empty) => {
      if (!dep?.gcomponentId) return;
      const targetIndex = (layers[layerIndex] || []).findIndex((item) => item?.id === dep.gcomponentId);
      if (targetIndex < 0) return;
      const target = layers[layerIndex][targetIndex];
      while (layer.length <= targetIndex) layer.push(null);
      layer[targetIndex] = {
        id: dep.gcomponentId,
        name: empty ? (target?.name || dep.name) : dep.name,
        code: target?.code,
        value: empty ? '' : dep,
      };
    };
    if (property?.dependentGcomponentItem) {
      applyDependent(dependentOf({ value: property }), false);
    } else {
      applyDependent(dependentOf(previous), true);
    }
    onChange?.({
      gglassId: value?.gglassId,
      gtypeId: value?.gtypeId,
      selectedGcomponentItems: rows,
    });
  };

  const handleMultiSlot = (layerIndex, slotIndex, component, propertyIds) => {
    const picked = (component.properties || []).filter((item) => propertyIds.includes(item.id));
    writeCell(layerIndex, slotIndex, component, picked);
  };

  const selectedRows = value?.selectedGcomponentItems || [];

  const templateControl = (
    <FormControl size="small" disabled={readOnly} sx={{ width: 160, maxWidth: '100%', flex: '0 0 auto', ...controlHeightSx }}>
      <InputLabel id="folder-glass-template-label">유리 템플릿</InputLabel>
      <Select
        labelId="folder-glass-template-label"
        label="유리 템플릿"
        value={glasses.some((glass) => glass.id === value?.gglassId) ? value.gglassId : ''}
        onChange={(e) => handleTemplate(e.target.value)}
      >
        {glasses.map((glass) => (
          <MenuItem key={glass.id} value={glass.id}>{glass.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const slotControls = [];
  layers.forEach((layer, layerIndex) => {
    (layer || []).forEach((component, slotIndex) => {
      if (!isProductSlot(component)) return;
      const cell = selectedRows[layerIndex]?.[slotIndex];
      const properties = component.properties || [];
      const key = `${layerIndex}-${component.id || slotIndex}`;
      if (component.multipleYN) {
        const currentIds = cellValueIds(cell).filter((id) => properties.some((property) => property.id === id));
        slotControls.push(
          <FormControl size="small" key={key} disabled={readOnly} sx={fieldSx(component.code, true)}>
            <InputLabel id={`folder-glass-slot-${layerIndex}-${slotIndex}`}>{component.name}</InputLabel>
            <Select
              multiple
              labelId={`folder-glass-slot-${layerIndex}-${slotIndex}`}
              label={component.name}
              value={currentIds}
              onChange={(e) => handleMultiSlot(layerIndex, slotIndex, component, e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => (
                    <Chip key={id} size="small" label={properties.find((property) => property.id === id)?.name || id} />
                  ))}
                </Box>
              )}
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>{property.name}</MenuItem>
              ))}
            </Select>
          </FormControl>,
        );
        return;
      }
      const current = cellValueId(cell);
      slotControls.push(
        <FormControl size="small" key={key} disabled={readOnly} sx={fieldSx(component.code)}>
          <InputLabel id={`folder-glass-slot-${layerIndex}-${slotIndex}`}>{component.name}</InputLabel>
          <Select
            labelId={`folder-glass-slot-${layerIndex}-${slotIndex}`}
            label={component.name}
            value={properties.some((property) => property.id === current) ? current : ''}
            onChange={(e) => handleSlot(layerIndex, slotIndex, component, e.target.value)}
          >
            <MenuItem value=""><em>없음</em></MenuItem>
            {properties.map((property) => (
              <MenuItem key={property.id} value={property.id}>{property.name}</MenuItem>
            ))}
            </Select>
          </FormControl>,
        );
    });
  });

  return (
    <>
    {showTitle && <Typography variant="body2" sx={{ mb: 0.5 }}>유리 구성</Typography>}
    <Box sx={inline ? { display: 'contents' } : rowSx}>
      {templateControl}
      {slotControls}
    </Box>
    {certificationId && glasses.length === 0 && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        이 인증규격에 연결된 유리 템플릿이 없습니다.
      </Typography>
    )}
    <AlertDialog alertInfo={alertInfo} setAlertInfo={setAlertInfo} />
    </>
  );
};

export default SubMatFolderGlassBom;
